import { useEffect, useLayoutEffect, useRef, useState } from "react"
import { motion, useMotionValueEvent } from "framer-motion"
import styled from "@emotion/styled"

import { useEnv } from "contexts/Env"
import { useLocale } from "contexts/Locale"
import { useViewport } from "contexts/Viewport"
import { useConsole } from "contexts/Console"

import getMediaQuery from "css/breakpoints"
import { fullGrid } from "css/grid"

import { handleVisitor } from "utils/handleVisitor.js"

import { colors } from "components/model/colors"
import { useValue } from "../context"
import AshlarReturnWishlist from "./AshlarReturnWishlist"
import AshlarReturnLastConfigured from "./AshlarReturnLastConfigured"
import AshlarReturnFamily from "./AshlarReturnFamily"
import AshlarReturnEvent from "./AshlarReturnEvent"
import AshlarReturnRetailer from "./AshlarReturnRetailer"
import { RollerProvider, useRoller } from "./context"
import Pagination from "./Pagination"
import Arrows from "./Arrows"
import { getIndex, getPropHeight } from "./../common"

const Li = styled(motion.li)`
  position: relative;
  justify-content: center;
  background-color: white;
  height: 300vh;
  --height: 100;
  margin-block-start: -100vh;

  &:last-of-type {
    height: 200vh;
  }
`

const RollerContnr = styled.div`
  height: 100vh;
  position: sticky;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  ${colors}
  ${fullGrid}
`

const List = styled.ul`
  grid-column: main;

  --rvwdth: 90vw;
  ${getMediaQuery("m")} {
    --rvwdth: 88vw;
  }

  scroll-snap-align: start;

  display: grid;
  grid-auto-flow: column;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
  ::-webkit-scrollbar {
    display: none;
  }

  grid-auto-columns: 100%;
  grid-template-rows: 80vh;
  gap: 10px;

  ${getMediaQuery("m")} {
    grid-template-rows: 75vh;
  }

  ${getMediaQuery("xxxl")} {
    max-width: 120rem;
    margin: 0 auto;
  }

  & > li {
    scroll-snap-align: start;
  }
`

const Overlay = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  height: 300vh;
  width: 100%;
  z-index: 1;
  grid-column: doc;
  grid-row: top-row / bottom-row;
  background: #212121;
  transform: translate3d(0, 0, 0);
  pointer-events: none;
`

const types = {
  wishlist: AshlarReturnWishlist,
  family: AshlarReturnFamily,
  retailer: AshlarReturnRetailer,
  configurator: AshlarReturnLastConfigured,
  eventcard: AshlarReturnEvent,
}

function Roller({ slides }) {
  const console = useConsole()

  const ref = useRef()
  const waEvtSended = useRef(false)
  const { slide, targetSlide, track } = useRoller()
  const { currentIndex, returning_pos } = useValue()

  function onTargetChange(t) {
    const firstEl = [...ref.current.children][0]
    const elTarg = [...ref.current.children][t].offsetLeft - firstEl.offsetLeft
    ref.current.scrollTo({
      top: 0,
      left: elTarg,
      behavior: "smooth",
    })
  }
  useMotionValueEvent(targetSlide, "change", onTargetChange)

  useLayoutEffect(() => {
    const r = ref.current

    const elements = [...r.children]

    function onIntersectionObserved(entries) {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          const curI = +entry.target.getAttribute("data-index")
          slide.set(curI)
        }
      }
    }
    const observer = new IntersectionObserver(onIntersectionObserved, {
      root: r,
      rootMargin: "0% 2% 0% 2%",
      threshold: 1,
    })

    for (const item of elements) observer.observe(item)

    return () => {
      for (const item of elements) observer.unobserve(item)
    }
  })

  function onIndex(i) {
    if (i + 1 === returning_pos && waEvtSended.current === false) {
      track({
        event: "Impression",
        customEvents: "returningVisitorDisplayed",
        index: slide.get(),
        ctaName: "",
      })
      waEvtSended.current = true
    }
  }
  useMotionValueEvent(currentIndex, "change", onIndex)

  useEffect(() => { }, [])

  return (
    <RollerContnr>
      <List ref={ref}>
        {slides.map((slide, i) => {
          const Slide = types[slide.type]
          return <Slide {...slide} key={slide.type} index={i} />
        })}
      </List>
    </RollerContnr>
  )
}

function VisitorAshlar({ slides }) {
  const { isMobile } = useViewport()
  const { updatePageNum, removePage, num, isReturnVisitor, returning_pos } = useValue()

  const [index, setIndex] = useState(null)
  const [isMob, setIsMob] = useState(isMobile.get())

  const ref = useRef()

  useLayoutEffect(() => {
    isReturnVisitor.set(returning_pos - 1)
    const index = getIndex(ref.current)
    updatePageNum("AshlarReturnVisitor", getPropHeight(ref.current), index)
    setIndex(index)
    return () => removePage("AshlarReturnVisitor")
  }, [])

  function onNumChange() {
    const index = getIndex(ref.current)
    setIndex(index)
  }
  useMotionValueEvent(num, "change", onNumChange)

  function onIsMobileChange(b) {
    setIsMob(b)
  }
  useMotionValueEvent(isMobile, "change", onIsMobileChange)

  function onFocus(e) {
    ref.current.scrollIntoView({ block: "center" })
  }

  return (
    <RollerProvider slides={slides}>
      <Li ref={ref} className='rvshlar' style={{ zIndex: 0 }} id={`ashlar--${index}`} data-scroll-pos='center' onFocusCapture={onFocus}>
        <Roller slides={slides} />
        {slides.length > 1 ? <Pagination nb={slides.length} /> : null}
        {slides.length > 1 && !isMob ? <Arrows nb={slides.length} /> : null}
        <Overlay initial={{ opacity: 1 }} animate={{ opacity: 0, transition: { delay: 0.5, duration: 0.9 } }} transition={{ type: "tween", duration: 1 }} />
      </Li>
    </RollerProvider>
  )
}

export default function AshlarReturnVisitor({ event_card }) {
  const console = useConsole()

  const locale = useLocale()
  const env = useEnv()

  const [storage, setStorage] = useState([])

  const getretailer = async rswi => {
    const response = await Promise.resolve(
      new URL(`/app/establishment/light/jsFront?rswi=${rswi}&countryCodeGeofencing=ALL&langCode=${locale.current.codes.ws}`, env.retailers)
    )
      .then(url => new Request(url))
      .then(request => fetch(request))
      .catch(err => err)
    const res = await response.json().catch(err => err)
    return { shortAddress: res.shortAddress, nameTranslated: res.nameTranslated }
  }

  const getmodel = async rmc => {
    const response = await Promise.resolve(
      new URL(`/api/catalog/config-2d/global?language=${locale.current.codes.ws}&rmc=${rmc}`, process.browser ? env.services : process.env.SERVICE_CATALOG_URL)
    )
      .then(url => new Request(url))
      .then(request => fetch(request))
      .catch(err => err)
    const res = await response.json().catch(err => err)
    return res?.results?.[0]?.watch.docs[0]
  }

  async function updateStorage() {
    const rv = localStorage?.getItem("returnVisitor")
    if (rv) {
      let rvparsed = JSON.parse(rv)
      const retailer = rvparsed.find(o => o.type === "retailer")
      const configurator = rvparsed.find(o => o.type === "configurator")

      if (retailer) {
        const rswi = retailer?.rswi
        const { shortAddress, nameTranslated } = await getretailer(rswi)
        if (shortAddress && nameTranslated) {
          Object.assign(rvparsed, Object.assign(retailer, { shortAddress, nameTranslated }))
        } else {
          rvparsed = rvparsed.filter(o => o.type !== "retailer")
        }
      }
      if (configurator) {
        const { spec_case, name, cover, majesty } = await getmodel(configurator.rmc)
        if (spec_case && name) {
          Object.assign(rvparsed, Object.assign(configurator, { familyName: name, description: spec_case, cover, majesty }))
        } else {
          rvparsed = rvparsed.filter(o => o.type !== "configurator")
        }
      }
      setStorage(rvparsed)
    }
  }

  useLayoutEffect(() => {
    const isEventRunning = event_card?.heading

    const wl = localStorage?.getItem("rmcWishList")
    const rv = localStorage?.getItem("returnVisitor")

    if (isEventRunning) {
      if (rv) {
        const rvparsed = JSON.parse(rv)
        const eventExists = rvparsed.find(o => o.type === "eventcard")
        if (!eventExists) {
          handleVisitor({ type: "eventcard", ...event_card })
        } else {
          const arr = rvparsed.map(obj => (obj.type === "eventcard" ? { type: "eventcard", ...event_card } : obj))
          localStorage.setItem("returnVisitor", JSON.stringify(arr))
        }
      } else {
        if (wl) {
          const wlarr = JSON.parse(wl)
          if (wlarr.length) {
            const rmcs = wlarr.reduce((acc, x) => acc.concat([x.productID]), [])
            handleVisitor({ type: "wishlist", list: rmcs })
          }
        }
        handleVisitor({ type: "eventcard", ...event_card })
      }
    } else {
      if (rv) {
        const rvparsed = JSON.parse(rv)
        const eventExists = rvparsed.find(o => o.type === "eventcard")
        if (eventExists) {
          const arr = rvparsed.filter(x => x.type !== "eventcard")
          localStorage.setItem("returnVisitor", JSON.stringify(arr))
        }
      } else {
        if (wl) {
          const wlarr = JSON.parse(wl)
          if (wlarr.length) {
            const rmcs = wlarr.reduce((acc, x) => acc.concat([x.productID]), [])
            handleVisitor({ type: "wishlist", list: rmcs })
          }
        }
      }
    }
    //
    updateStorage()
    window.addEventListener("storage", updateStorage)
    return () => window.removeEventListener("storage", updateStorage)
  }, [])

  return storage.length ? <VisitorAshlar slides={storage} /> : null
}
